// FIXME
// eslint-disable-next-line workspaces/require-dependency
import { type DefaultRootState } from '@databases/redux/default-root-state';
import { type ObjectMode, type Optional } from '@databases/types';
import {
  BacklinkDisplayType,
  type Database,
  type DatabaseField,
  DatabaseFieldType,
  type EntryWithContext,
  type FieldWithContext
} from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';
import { isEqual } from 'lodash-es';
import { createCachedSelector } from 're-reselect';

import { RECURSION_LIMIT } from '../../constants.ts';
import { selectBaseUrl } from '../../redux/context/selectors.ts';
import { loadDatabaseByContentId } from '../../redux/databases/actions.ts';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByIdOptional
} from '../../redux/databases/selectors.ts';
import { getEntryPath } from '../../urls.ts';
import { encodeHtml, getUniqueObjectId } from '../../utils.ts';
import { type FieldType, loadDependencies } from '../field-types.ts';
import {
  getDatabaseEntries,
  getDatabaseEntryOptional,
  getDatabaseFields,
  getDatabaseValue,
  getEntryName,
  getFirstField,
  isBrokenDatabase
} from '../utils.ts';

import { getEntryLinkTargetEntries } from './entryLink.ts';

/**
 * Generate the index key that is used in the map returned by selectReferencingEntries().
 */
function getReferencingEntryKey({
  database,
  entry
}: EntryWithContext<ObjectMode>): string {
  return `${database.space.key}-${database.id}-${entry.id}`;
}

/**
 * A memoized selector that walks through each entry in the given database and checks which entries of other databases
 * it references through entry link values. The result can be used by backlink fields to quickly figure out which
 * entries of the target database reference a given entry in the source database. To achieve this quick lookup, this
 * selector returns the entry references in reverse: The key is the entry in the source database (in string form
 * as returned by getReferencingEntryKey()) and the value is a list of entries in the target database that reference
 * that entry.
 */
const selectReferencingEntries = createCachedSelector<
  DefaultRootState,
  {
    database: Database<ObjectMode>;
    selectDatabase: typeof selectDatabaseByContentIdOptional;
    recursionCount: number;
  },
  DefaultRootState,
  Database<ObjectMode>,
  typeof selectDatabaseByContentIdOptional,
  number,
  Map<string, EntryWithContext<ObjectMode>[]>
>(
  (state) => state,
  (state, { database }) => database,
  (state, { selectDatabase }) => selectDatabase,
  (state, { recursionCount }) => recursionCount,
  (state, database, selectDatabase) => {
    const entryFields = getDatabaseFields(database).filter(
      (field): field is DatabaseField<DatabaseFieldType.ENTRY_LINK> =>
        field.type === DatabaseFieldType.ENTRY_LINK
    );

    const result = new Map<string, EntryWithContext<ObjectMode>[]>();
    for (const entry of getDatabaseEntries(database)) {
      const entryWithContext = { database, entry };

      const targetEntries = entryFields.flatMap((field) => {
        const targetValue = getDatabaseValue(database, field, entry);
        if (targetValue.type === DatabaseFieldType.ENTRY_LINK) {
          return getEntryLinkTargetEntries({ field, value: targetValue.value }, state, {
            selectDatabase
          });
        }

        return [];
      });

      for (const key of [
        ...new Set(
          targetEntries.map((targetEntry) => getReferencingEntryKey(targetEntry))
        )
      ]) {
        if (!result.has(key)) {
          result.set(key, []);
        }
        result.get(key)!.push(entryWithContext);
      }
    }

    return result;
  }
)(
  (state, { database, selectDatabase, recursionCount }) =>
    `${database.id}-${getUniqueObjectId(selectDatabase)}-${recursionCount}`
);

/**
 * Returns the entries (with context) from the target database that reference the given entry. The list of references entries in the
 * target database is memoized to avoid recalculating it for each backlink field. This means that the selectDatabase parameter
 * MUST be a memoized function that doesn't change identity on each call, as otherwise the cache is missed.
 */
export function getBacklinkEntries<State extends DefaultRootState>(
  {
    database,
    field,
    entry
  }: Optional<
    FieldWithContext<DatabaseFieldType, ObjectMode> & EntryWithContext<ObjectMode>,
    'entry'
  >,
  state: State | undefined,
  {
    selectDatabase = selectDatabaseByContentIdOptional,
    recursionCount = 0
  }: {
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
    recursionCount?: number;
  } = {}
): EntryWithContext[] {
  if (!entry || !state) {
    return [];
  }
  let _db = undefined;
  if (field.settings.databaseSpace && field.settings.databaseId) {
    _db = selectDatabaseByIdOptional(state, {
      spaceKey: field.settings.databaseSpace.key,
      id: field.settings.databaseId
    });
  } else if (field.settings.contentId) {
    _db = selectDatabaseByContentIdOptional(state, {
      contentId: field.settings.contentId
    });
  }
  const targetDatabase = _db && selectDatabase(state, { contentId: _db.contentId });
  if (!targetDatabase || isBrokenDatabase(targetDatabase)) {
    return [];
  }

  const referencingEntries = selectReferencingEntries(state, {
    database: targetDatabase,
    selectDatabase: selectDatabase as any,
    recursionCount
  });
  return referencingEntries.get(getReferencingEntryKey({ database, entry })) ?? [];
}

const backlinkFieldType: FieldType<DatabaseFieldType.BACKLINK> = {
  supportedFilterTypes: [DatabaseFilterType.CONTAINS],

  isCsvImportSupported: false,

  renderExportView: (
    { database, field, entry },
    state,
    renderContext,
    recursionCount
  ) => {
    if (recursionCount >= RECURSION_LIMIT) {
      return '';
    }

    const targetEntries = getBacklinkEntries({ database, field, entry }, state, {
      recursionCount
    });
    switch (field.settings.backlinkDisplayType) {
      case BacklinkDisplayType.NUMBER:
        return `${targetEntries.length}`;

      default: {
        const baseUrl = selectBaseUrl(state);
        return targetEntries
          .map(
            (targetEntry) =>
              `<a href="${encodeHtml(
                `${baseUrl}${getEntryPath({
                  contentId: targetEntry.database.contentId,
                  entryId: targetEntry.entry.id,
                  spaceKey: database.space.key
                })}`
              )}">${encodeHtml(getEntryName(targetEntry, state, { recursionCount }))}</a>`
          )
          .join('<br/>');
      }
    }
  },

  getInitialValue: () => undefined,

  isEmptyValue: ({ database, field, entry }, state, recursionCount) => {
    if (field.settings.backlinkDisplayType === BacklinkDisplayType.NUMBER) {
      return false;
    }

    if (!entry || !state) {
      return true;
    }

    const targetEntries = getBacklinkEntries({ database, field, entry }, state, {
      recursionCount
    });
    return targetEntries.length === 0;
  },

  isEqualValue: (context1, context2, state, recursionCount) => {
    const targetEntries1 = context1.entry
      ? getBacklinkEntries(context1, state, { recursionCount })
      : [];
    const targetEntries2 = context2.entry
      ? getBacklinkEntries(context2, state, { recursionCount })
      : [];
    return isEqual(
      new Set(targetEntries1.map((e) => `${e.database.contentId}-${e.entry.id}`)),
      new Set(targetEntries2.map((e) => `${e.database.contentId}-${e.entry.id}`))
    );
  },

  getStringRepresentation: (
    { database, field, entry },
    state,
    { recursionCount },
    defaultEntryName
  ) => {
    if (recursionCount >= RECURSION_LIMIT || !state) {
      return '';
    }

    const targetEntries = getBacklinkEntries({ database, field, entry }, state, {
      recursionCount
    });
    switch (field.settings.backlinkDisplayType) {
      case BacklinkDisplayType.NUMBER:
        return `${targetEntries.length}`;

      default:
        return targetEntries
          .map(
            (targetEntry) =>
              getEntryName(targetEntry, state, { recursionCount }) ||
              targetEntry.entry.id ||
              defaultEntryName
          )
          .join('\n,');
    }
  },

  getNumberRepresentation: ({ database, field, entry }, state, recursionCount) => {
    if (
      !state ||
      recursionCount >= RECURSION_LIMIT ||
      field.settings.backlinkDisplayType !== BacklinkDisplayType.NUMBER
    ) {
      return undefined;
    }

    return getBacklinkEntries({ database, field, entry }, state, { recursionCount })
      .length;
  },

  loadDependencies: (
    { contentId, field, entryId },
    { dispatch, selector, selectDatabase, helpers },
    state,
    { recursionCount }
  ) => {
    if (field.settings.contentId) {
      dispatch(loadDatabaseByContentId(field.settings.contentId));

      if (entryId && field.settings.backlinkDisplayType !== BacklinkDisplayType.NUMBER) {
        // Load main field dependencies of backlink entries
        const targetValues = selector((state) => {
          const rawDatabase = selectDatabase(state, { contentId });
          const validatedDatabase =
            rawDatabase && !isBrokenDatabase(rawDatabase) ? rawDatabase : undefined;
          const entry =
            validatedDatabase && getDatabaseEntryOptional(validatedDatabase, entryId);
          const targetEntries =
            validatedDatabase && entry
              ? getBacklinkEntries({ database: validatedDatabase, field, entry }, state, {
                  recursionCount
                })
              : [];
          return targetEntries.flatMap((targetEntry) => {
            const targetFirstField = getFirstField(targetEntry.database);
            const targetValue =
              targetFirstField &&
              getDatabaseValue(targetEntry.database, targetFirstField, targetEntry.entry);
            return targetValue
              ? [
                  {
                    contentId: targetEntry.database.contentId,
                    field: targetFirstField,
                    entryId: targetEntry.entry.id,
                    value: targetValue
                  }
                ]
              : [];
          });
        });
        for (const context of targetValues) {
          loadDependencies(context, helpers, state, {
            recursionCount: recursionCount + 1
          });
        }
      }
    }
  },

  noDefaultValue: true
};

export default backlinkFieldType;
